import React from 'react'
import PropTypes from 'prop-types'
import { useController } from 'react-hook-form'

// 🚨 Experimental field component for React Hook Form 🚨

const formatValidationRules = (validation, validationName) => {
  if (!validation) return undefined

  if (Array.isArray(validation)) {
    return validation.reduce((rulesObj, validationRule, index) => {
      // It doesn't make sense to create a new object just to appease the linter (performance and this is internally pure).
      // eslint-disable-next-line no-param-reassign
      rulesObj[`${validationName}-${index}`] = v => validationRule(v)
      return rulesObj
    }, {})
  }

  return { [validationName]: v => validation(v) }
}

const ControlledHookField = ({
  component: InputComponent,
  name,
  control,
  validate,
  warn,
  onChange: propsOnChange,
  onBlur: propsOnBlur,
  ...props
}) => {
  const errorRules = formatValidationRules(validate, 'error')
  const warnRules = formatValidationRules(warn, 'warn')

  const rules = {
    validate: {
      ...errorRules,
      ...warnRules,
    },
  }

  const {
    field: { name: fieldName, onChange, onBlur, value },
    fieldState: { isTouched, error },
    formState: { isSubmitted },
  } = useController({
    name,
    control,
    rules,
  })

  const onChangeHandler = (eventOrValue, ...args) => {
    onChange(eventOrValue)
    if (propsOnChange) propsOnChange(eventOrValue, args)
  }

  const onBlurHandler = (eventOrValue, ...args) => {
    onBlur(eventOrValue)
    if (propsOnBlur) propsOnBlur(eventOrValue, args)
  }

  // Cast as wide a net as possible – add input object AND onChange and onBlur and value
  const input = {
    value,
    onChange: onChangeHandler,
    onBlur: onBlurHandler,
  }

  const constructedMeta = {
    touched: isTouched || isSubmitted,
    error: error && error.type.includes('error') ? error.message : undefined,
    warn: error && error.type.includes('warn') ? error.message : undefined,
  }

  return (
    <InputComponent
      name={fieldName}
      input={input}
      value={value}
      onChange={onChangeHandler}
      onBlur={onBlurHandler}
      meta={constructedMeta}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  )
}

ControlledHookField.propTypes = {
  component: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  control: PropTypes.any.isRequired,
  validate: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.func),
  ]),
  warn: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.func),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
}

export default ControlledHookField
